import React, { createContext, useContext, useReducer, useEffect } from 'react';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  component: string;
  isCompleted: boolean;
  isOptional?: boolean;
  requiredSteps?: string[];
}

export type OnboardingFlow = 'developer' | 'business' | 'analyst';

export interface OnboardingState {
  isActive: boolean;
  currentStepId: string | null;
  steps: OnboardingStep[];
  completedSteps: string[];
  skippedSteps: string[];
  userType: OnboardingFlow | null;
  progress: number;
  isCompleted: boolean;
  startedAt: Date | null;
  completedAt: Date | null;
}

type OnboardingAction =
  | { type: 'START_ONBOARDING'; userType: 'developer' | 'business' | 'analyst' }
  | { type: 'COMPLETE_STEP'; stepId: string }
  | { type: 'SKIP_STEP'; stepId: string }
  | { type: 'GO_TO_STEP'; stepId: string }
  | { type: 'RESTART_ONBOARDING' }
  | { type: 'FINISH_ONBOARDING' }
  | { type: 'LOAD_STATE'; state: Partial<OnboardingState> };

const defaultSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to MCP Platform',
    description: 'Get started with the Model Context Protocol management platform',
    component: 'WelcomeStep',
    isCompleted: false,
  },
  {
    id: 'profile-setup',
    title: 'Set Up Your Profile',
    description: 'Configure your account and preferences',
    component: 'ProfileSetupStep',
    isCompleted: false,
  },
  {
    id: 'app-discovery',
    title: 'Discover Apps',
    description: 'Explore 2,500+ integrations available through Pipedream',
    component: 'AppDiscoveryStep',
    isCompleted: false,
  },
  {
    id: 'first-connection',
    title: 'Create Your First Connection',
    description: 'Connect to your first service like Gmail or Slack',
    component: 'FirstConnectionStep',
    isCompleted: false,
  },
  {
    id: 'connection-planning',
    title: 'Plan Your Connections',
    description: 'Learn to use the visual connection planner',
    component: 'ConnectionPlanningStep',
    isCompleted: false,
    isOptional: true,
  },
  {
    id: 'config-generation',
    title: 'Generate MCP Configuration',
    description: 'Create your first MCP configuration for Claude Desktop',
    component: 'ConfigGenerationStep',
    isCompleted: false,
  },
  {
    id: 'analytics-overview',
    title: 'Monitor Your Setup',
    description: 'Learn about analytics and monitoring features',
    component: 'AnalyticsOverviewStep',
    isCompleted: false,
    isOptional: true,
  },
  {
    id: 'completion',
    title: 'You\'re All Set!',
    description: 'Congratulations on completing the onboarding',
    component: 'CompletionStep',
    isCompleted: false,
  },
];

const initialState: OnboardingState = {
  isActive: false,
  currentStepId: null,
  steps: defaultSteps,
  completedSteps: [],
  skippedSteps: [],
  userType: null,
  progress: 0,
  isCompleted: false,
  startedAt: null,
  completedAt: null,
};

function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'START_ONBOARDING':
      return {
        ...state,
        isActive: true,
        userType: action.userType,
        currentStepId: state.steps[0]?.id || null,
        startedAt: new Date(),
        steps: getStepsForUserType(action.userType),
      };

    case 'COMPLETE_STEP': {
      const updatedSteps = state.steps.map(step =>
        step.id === action.stepId ? { ...step, isCompleted: true } : step
      );
      const completedSteps = [...state.completedSteps, action.stepId].filter(
        (id, index, arr) => arr.indexOf(id) === index
      );
      const currentStepIndex = state.steps.findIndex(s => s.id === state.currentStepId);
      const nextStep = state.steps[currentStepIndex + 1];
      const progress = (completedSteps.length / state.steps.filter(s => !s.isOptional).length) * 100;

      return {
        ...state,
        steps: updatedSteps,
        completedSteps,
        currentStepId: nextStep?.id || null,
        progress: Math.min(progress, 100),
        isCompleted: !nextStep,
        completedAt: !nextStep ? new Date() : null,
      };
    }

    case 'SKIP_STEP': {
      const skippedSteps = [...state.skippedSteps, action.stepId].filter(
        (id, index, arr) => arr.indexOf(id) === index
      );
      const currentStepIndex = state.steps.findIndex(s => s.id === state.currentStepId);
      const nextStep = state.steps[currentStepIndex + 1];

      return {
        ...state,
        skippedSteps,
        currentStepId: nextStep?.id || null,
        isCompleted: !nextStep,
        completedAt: !nextStep ? new Date() : null,
      };
    }

    case 'GO_TO_STEP':
      return {
        ...state,
        currentStepId: action.stepId,
      };

    case 'RESTART_ONBOARDING':
      return {
        ...initialState,
        isActive: true,
        userType: state.userType,
        currentStepId: state.steps[0]?.id || null,
        startedAt: new Date(),
      };

    case 'FINISH_ONBOARDING':
      return {
        ...state,
        isActive: false,
        isCompleted: true,
        completedAt: new Date(),
      };

    case 'LOAD_STATE':
      return {
        ...state,
        ...action.state,
      };

    default:
      return state;
  }
}

function getStepsForUserType(userType: 'developer' | 'business' | 'analyst'): OnboardingStep[] {
  // Customize steps based on user type
  const baseSteps = [...defaultSteps];
  
  if (userType === 'business') {
    // Business users might skip technical steps
    return baseSteps.map(step => 
      step.id === 'connection-planning' || step.id === 'analytics-overview'
        ? { ...step, isOptional: true }
        : step
    );
  }
  
  if (userType === 'analyst') {
    // Analysts might focus more on analytics
    return baseSteps.map(step => 
      step.id === 'analytics-overview'
        ? { ...step, isOptional: false }
        : step
    );
  }
  
  return baseSteps;
}

interface OnboardingContextType {
  state: OnboardingState;
  startOnboarding: (userType: 'developer' | 'business' | 'analyst') => void;
  completeStep: (stepId: string) => void;
  skipStep: (stepId: string) => void;
  goToStep: (stepId: string) => void;
  restartOnboarding: () => void;
  finishOnboarding: () => void;
  getCurrentStep: () => OnboardingStep | null;
  getNextStep: () => OnboardingStep | null;
  getPreviousStep: () => OnboardingStep | null;
  canGoToStep: (stepId: string) => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

const STORAGE_KEY = 'mcp-platform-onboarding';

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);

  // Load saved state on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const savedState = JSON.parse(saved);
        dispatch({ type: 'LOAD_STATE', state: savedState });
      } catch (error) {
        console.error('Failed to load onboarding state:', error);
      }
    }
  }, []);

  // Save state changes
  useEffect(() => {
    if (state.isActive || state.isCompleted) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  const startOnboarding = (userType: 'developer' | 'business' | 'analyst') => {
    dispatch({ type: 'START_ONBOARDING', userType });
  };

  const completeStep = (stepId: string) => {
    dispatch({ type: 'COMPLETE_STEP', stepId });
  };

  const skipStep = (stepId: string) => {
    dispatch({ type: 'SKIP_STEP', stepId });
  };

  const goToStep = (stepId: string) => {
    dispatch({ type: 'GO_TO_STEP', stepId });
  };

  const restartOnboarding = () => {
    dispatch({ type: 'RESTART_ONBOARDING' });
  };

  const finishOnboarding = () => {
    dispatch({ type: 'FINISH_ONBOARDING' });
  };

  const getCurrentStep = (): OnboardingStep | null => {
    return state.steps.find(step => step.id === state.currentStepId) || null;
  };

  const getNextStep = (): OnboardingStep | null => {
    const currentIndex = state.steps.findIndex(step => step.id === state.currentStepId);
    return state.steps[currentIndex + 1] || null;
  };

  const getPreviousStep = (): OnboardingStep | null => {
    const currentIndex = state.steps.findIndex(step => step.id === state.currentStepId);
    return currentIndex > 0 ? state.steps[currentIndex - 1] : null;
  };

  const canGoToStep = (stepId: string): boolean => {
    const step = state.steps.find(s => s.id === stepId);
    if (!step) return false;

    // Can always go to completed steps
    if (state.completedSteps.includes(stepId)) return true;

    // Check if required steps are completed
    if (step.requiredSteps) {
      return step.requiredSteps.every(reqId => state.completedSteps.includes(reqId));
    }

    // Can go to any step if no requirements
    return true;
  };

  const value: OnboardingContextType = {
    state,
    startOnboarding,
    completeStep,
    skipStep,
    goToStep,
    restartOnboarding,
    finishOnboarding,
    getCurrentStep,
    getNextStep,
    getPreviousStep,
    canGoToStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}